/* eslint-disable no-magic-numbers */
/* eslint-disable complexity */
import { useCallback, useMemo, useState } from 'react';
import { toastError } from '@futrli/ui-components';
import { useIntl } from 'react-intl';
import axios from 'axios';

import { getErrorMessage, Logger } from '@futrli/shared-services';
import sleep from '@futrli/shared-services/api-client/utils/getters';
import { ExcelDriverValidationFailureResponse, ExcelDriverValidationSuccessResponse } from '@futrli-sdk/predict';
import { getExcelUploadPresignedUrl, getFileScanStatus } from '@futrli/predict/core/api/organisations';
import { generateDriver, getExcelFileValidation } from '@futrli/predict/core/api/driver';
import { getExcelValidationErrorMessage } from './utils';

export type StepType = 'ready' | 'upload' | 'scan' | 'validation' | 'create' | 'update';

export const useUploadDrivers = (workspaceId: string, organisationId: string) => {
  const intl = useIntl();
  const [uploadFailed, setUploadFailed] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [isInProgress, setIsInProgress] = useState(false);
  const [step, setStep] = useState<StepType>('ready');
  const [errorMessage, setErrorMessage] = useState('');
  const [warningMessage, setWarningMessage] = useState('');

  const errorsDictionary = useMemo(
    () => ({
      DRIVER_SHEET_MISSING: intl.formatMessage({
        id: 'error.excel_drivers.driver_sheet_missing',
        defaultMessage:
          "File uploaded must contain a sheet named 'Drivers'. Excel file does not have a sheet named 'Drivers'"
      }),
      ORGANISATION_NAME_DO_NOT_MATCH: intl.formatMessage({
        id: 'error.excel_drivers.organisation_name_do_not_match',
        defaultMessage: 'Company name does not match the organisation the driver is being imported to'
      }),
      TYPE_MUST_BE_DRIVER: intl.formatMessage({
        id: 'error.excel_drivers.type_must_be_driver',
        defaultMessage: "Import type in cell B1 must be 'Drivers'. Import type is invalid"
      }),
      DATE_FORMAT_INCORRECT: intl.formatMessage({
        id: 'error.excel.driver_date_format_incorrect',
        defaultMessage:
          'Row 5 should only contain dates in MMM-YY format where there are prediction values. Value is not a date'
      }),
      DATE_ROW_HAD_BLANKS: intl.formatMessage({
        id: 'error.excel.date_row_had_blanks',
        defaultMessage:
          'Row 5 should only contain dates in MMM-YY format where there are prediction values. Date is blank'
      }),
      NOT_CONSECUTIVE_DATES: intl.formatMessage({
        id: 'error.excel.not_consecutive_dates',
        defaultMessage: 'Dates in row 5 must be consecutive. Date is not consecutive'
      }),
      DRIVER_NAME_IS_BLANK_BUT_RELATED_FIELDS_POPULATED: intl.formatMessage({
        id: 'error.excel_drivers.driver_name_blank_related_fields_populated',
        defaultMessage: 'Driver name is blank, but driver data exists on this row'
      }),
      DRIVER_NAME_TOO_LONG: intl.formatMessage({
        id: 'error.excel_drivers.driver_name_too_long',
        defaultMessage: 'Driver name is too long. Maximum is 30 characters'
      }),
      DRIVER_NAME_NOT_UNIQUE: intl.formatMessage({
        id: 'error.excel_drivers.driver_name_not_unique',
        defaultMessage: 'Driver names must be unique. Driver name is not unique'
      }),
      DRIVER_GROUP_NAME_TOO_LONG: intl.formatMessage({
        id: 'error.excel_drivers.driver_group_name_too_long',
        defaultMessage: 'Driver group name is too long. Maximum is 30 characters'
      }),
      OUTPUT_OCCURS_ON_DAY_NOT_VALID: intl.formatMessage({
        id: 'error.excel_drivers.output_occurs_on_day_not_valid',
        defaultMessage:
          "Valid 'Store on' dates in column C are 1-31 or 'Last day of the month', with blank defaulting to the 1st of the month. Date is invalid"
      }),
      DECIMAL_DISPLAY_NOT_VALID: intl.formatMessage({
        id: 'error.excel_drivers.decimal_display_not_valid',
        defaultMessage: 'Please select a decimal display option from the dropdown. Format chosen is invalid'
      }),
      AMOUNT_OUT_OF_RANGE: intl.formatMessage({
        id: 'error.excel_drivers.amount_error',
        defaultMessage:
          'Driver must be in the range -9,999,999,999.999999 to +9,999,999,999.999999 and no more than 6 decimal places. Driver value is invalid'
      }),
      AMOUNT_NOT_NUMBER: intl.formatMessage({
        id: 'error.excel_drivers.amount_error',
        defaultMessage:
          'Driver must be in the range -9,999,999,999.999999 to +9,999,999,999.999999 and no more than 6 decimal places. Driver value is invalid'
      }),
      AMOUNT_NOT_ROUNDED: intl.formatMessage({
        id: 'error.excel_drivers.amount_error',
        defaultMessage:
          'Driver must be in the range -9,999,999,999.999999 to +9,999,999,999.999999 and no more than 6 decimal places. Driver value is invalid'
      }),
      INVALID_FILE: intl.formatMessage({
        id: 'error.invalid_file',
        defaultMessage: 'Invalid file'
      })
    }),
    [intl]
  );

  const warningsDictionary = useMemo(
    () => ({
      DATE_OUTSIDE_ALLOWED_RANGE: intl.formatMessage({
        id: 'warning.date_outside_allowed_range',
        defaultMessage: 'Warning: File contains date outside of allowed range.'
      })
    }),
    [intl]
  );

  const sheetName = useMemo(
    () => ({
      DRIVER: intl.formatMessage({
        id: 'reporting.grouping.drivers',
        defaultMessage: 'Drivers'
      })
    }),
    [intl]
  );

  const handleError = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (e: any) => {
      Logger.error(e as string);
      let errorMessage = getErrorMessage(e);
      if (typeof errorMessage === 'string' && errorMessage.toLocaleLowerCase() === 'network error') {
        errorMessage = intl.formatMessage({
          id: 'error.failed_upload_file_open_other_app',
          defaultMessage:
            'Failed to upload the file. Please ensure the file is not open in another application, then try again.'
        });
      }
      toastError(getErrorMessage(errorMessage));
      setUploadFailed(true);
    },
    [intl]
  );

  const uploadScanValidateExcelFile = useCallback(
    async (selectedFile: File) => {
      setErrorMessage('');
      setWarningMessage('');
      if (!selectedFile) {
        toastError(intl.formatMessage({ id: 'common.please_select_file', defaultMessage: 'Please select a file' }));
      }
      setStep('upload');
      setIsInProgress(true);
      setUploadFailed(false);
      setUploadSuccess(false);
      const res = await getExcelUploadPresignedUrl(workspaceId, selectedFile.name, 'driver');
      if (res.excel_file_id && res.upload_url?.url) {
        const formData = new FormData();
        Object.entries(res.upload_url.fields).forEach(([field, value]) => {
          formData.append(field, value as string);
        });
        formData.append('Content-Type', selectedFile.type);
        formData.append('file', selectedFile);
        await axios.post(res.upload_url.url, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        // scan status
        setStep('scan');
        let scanStatus = { scan_status: 'pending' };
        while (scanStatus.scan_status === 'pending') {
          scanStatus = await getFileScanStatus(workspaceId, res.excel_file_id);
          await sleep(500);
        }
        if (scanStatus.scan_status !== 'clean') {
          setErrorMessage(
            intl.formatMessage({
              id: 'error.scan_error_description',
              defaultMessage: 'We found a problem when scanning your file. Please check the file and try again.'
            })
          );
          throw new Error(
            intl.formatMessage({
              id: 'error.scan_error',
              defaultMessage: 'File scan failed.'
            })
          );
        }
        setStep('validation');
        const validationStatus = await getExcelFileValidation(workspaceId, organisationId, res.excel_file_id);
        if ((validationStatus as ExcelDriverValidationSuccessResponse)?.warning_code) {
          const warning = validationStatus as ExcelDriverValidationSuccessResponse;
          setWarningMessage(warningsDictionary[warning.warning_code]);
        }
        if ((validationStatus as ExcelDriverValidationFailureResponse)?.reason_code) {
          const error = validationStatus as ExcelDriverValidationFailureResponse;
          const reasonMessage = errorsDictionary[error.reason_code];

          const errorString = getExcelValidationErrorMessage(intl, reasonMessage, sheetName, error.sheet, error.cell);

          setErrorMessage(errorString);
          throw new Error(
            intl.formatMessage({
              id: 'error.validation_error',
              defaultMessage: 'File validation failed.'
            })
          );
        }
      }
      return res.excel_file_id;
    },
    [workspaceId, intl, organisationId, errorsDictionary, sheetName, warningsDictionary]
  );

  const createDriverFromExcelFile = useCallback(
    async (selectedFile: File) => {
      try {
        const fileId = await uploadScanValidateExcelFile(selectedFile);
        setStep('create');
        await generateDriver(workspaceId, organisationId, fileId);
        setUploadSuccess(true);
      } catch (e) {
        handleError(e);
      } finally {
        setIsInProgress(false);
      }
    },
    [handleError, organisationId, uploadScanValidateExcelFile, workspaceId]
  );

  return {
    createDriverFromExcelFile,
    uploadFailed,
    uploadSuccess,
    setUploadSuccess,
    isInProgress,
    step,
    setStep,
    errorMessage,
    setErrorMessage,
    warningMessage,
    setWarningMessage
  };
};
